import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import ProtectedRoute from "./routes/ProtectedRoute";

import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider } from "./contexts/AuthContext";
import { FontReadyProvider } from "./contexts/FontReadyContext";
import { UserProfileProvider } from "./contexts/UserProfileContext";
import { TaskProvider } from "./contexts/TaskContext";
import { WorkspaceModalProvider } from "./contexts/WorkspaceModalContext";

import NativeNavigationListener from "./components/NativeNavigationListener";
import GlobalHotkeys from "./components/GlobalHotkeys";
import TidleLoadingSpinner from "./components/TidleLoadingSpinner";
import { DevSubscriptionOverrideProvider } from "./contexts/DevSubscriptionOverrideContext";
import DevSubscriptionSwitch from "./components/DevSubscriptionSwitch";
import TidleApiStatus from "./components/TidleApiStatus";
import FirebaseBackendSwitch from "./components/FirebaseBackendSwitch";

import RedirectPage from "./pages/RedirectPage";

const SupportPage = lazy(() => import("./pages/SupportPage"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const LandingPage2 = lazy(() => import("./pages/LandingPage2"));
const MainPage = lazy(() => import("./pages/Main/MainPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const SubscribePage = lazy(() => import("./pages/SubscribePage"));
const AnalyticsPage = lazy(() => import("./pages/Analytics/AnalyticsPage"));
const SettingsPage = lazy(() => import("./pages/Settings/SettingsPage"));
const AppleUIPage = lazy(() => import("./pages/AppleUIPage"));
const CategoryTestPage = lazy(() => import("./pages/CategoryTestPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const PasswordResetPage = lazy(() => import("./pages/PasswordResetPage"));
const NewPasswordPage = lazy(() => import("./pages/NewPasswordPage"));
const UpgradePage = lazy(() => import("./pages/UpgradePage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ConnectMcpPage = lazy(() => import("./pages/ConnectMcpPage"));
const OpenMainRedirect = lazy(() => import("./pages/OpenMainRedirect"));

// Single shell that controls scrolling for the whole app
// Use 100% so it fills #root (fixes bottom gap when #root is zoomed on desktop)
const AppShell = ({ children }) => {
  return (
    <div
      style={{
        height: "100%",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          height: "100%",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch", // smooth on iOS
        }}
      >
        {children}
      </div>
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <FontReadyProvider>
          <UserProfileProvider>
          <TaskProvider>
            <DevSubscriptionOverrideProvider>
            <WorkspaceModalProvider>
            <AppShell>
              <NativeNavigationListener />
              <GlobalHotkeys />
              <Suspense fallback={<TidleLoadingSpinner />}>
              <Routes>
                <Route path="/" element={<RedirectPage />} />
                <Route path="/landing" element={<LandingPage />} />
                <Route path="/landing2" element={<LandingPage2 />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/reset-password" element={<PasswordResetPage />} />
                <Route path="/new-password" element={<NewPasswordPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/support" element={<SupportPage />} />
                <Route path="/connect-mcp" element={<ConnectMcpPage />} />
                <Route path="/open-main" element={<OpenMainRedirect />} />

                {/* Protected Routes */}
                <Route
                  path="/main"
                  element={
                    <ProtectedRoute>
                      <MainPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/analytics"
                  element={
                    <ProtectedRoute>
                      <AnalyticsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <SettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/subscribe"
                  element={
                    <ProtectedRoute>
                      <SubscribePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/upgrade"
                  element={
                    <ProtectedRoute>
                      <UpgradePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminDashboard />
                    </ProtectedRoute>
                  }
                />

                {/* Test Pages */}
                <Route path="/test-ui" element={<AppleUIPage />} />
                <Route path="/test-category" element={<CategoryTestPage />} />
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
              </Suspense>
              {process.env.NODE_ENV === "development" && (
                <div
                  style={{
                    position: "fixed",
                    bottom: 12,
                    right: 12,
                    zIndex: 9999,
                    display: "flex",
                    flexDirection: "row-reverse",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <DevSubscriptionSwitch />
                  <FirebaseBackendSwitch />
                  <TidleApiStatus />
                </div>
              )}
            </AppShell>
            </WorkspaceModalProvider>
            </DevSubscriptionOverrideProvider>
          </TaskProvider>
          </UserProfileProvider>
          </FontReadyProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
    </HelmetProvider>
  );
}

export default App;
